/**
 * ICD-10-CM diagnosis code picker.
 *
 * Typeahead combobox backed by /viewer-api/icd10/search. Stored
 * value is a JSON array so a report can carry multiple codes
 * (principal + additional). Chip row renders picked codes; each
 * chip has a ✕ that dispatches a `revoke` change so the parent
 * can audit the removal.
 *
 * Change events:
 *   onChange(nextValue, event?)
 *     - event = { action: "add",    code }   — user picked a code
 *     - event = { action: "remove", code }   — user clicked a chip ✕
 *     - event = undefined                    — bulk replace (hydration)
 *
 * Parent (SynopticPanel) bubbles the full body + diff event up to
 * PathologyShell which emits report.icd10_assign / _revoke with
 * before/after metadata (FDA §11.10(e)).
 */
import { For, Show, createEffect, createSignal, onCleanup } from "solid-js";

export interface IcdValue {
  code:         string;        // "C50911"
  code_dotted:  string;        // "C50.911"
  long_desc:    string;
  principal?:   boolean;
  added_at?:    string;        // ISO 8601 when the code was attached
}

interface Hit {
  code: string; code_dotted: string; short_desc: string; long_desc: string;
  is_billable: boolean; chapter: string | null; block: string | null;
}

export interface Icd10ChangeEvent {
  action: "add" | "remove" | "principal";
  code:   string;
}

export function Icd10Combobox(p: {
  value: IcdValue[];
  onChange: (next: IcdValue[], ev?: Icd10ChangeEvent) => void;
  hint?: string;
}) {
  const [q, setQ]           = createSignal("");
  const [chapter, setChap]  = createSignal<string>("");
  const [hits, setHits]     = createSignal<Hit[]>([]);
  const [open, setOpen]     = createSignal(false);
  const [loading, setLoad]  = createSignal(false);
  const [chapters, setChs]  = createSignal<{ chapter: string; billable: number }[]>([]);

  // Chapters for the filter chip row — fetched once.
  createEffect(async () => {
    try {
      const auth = JSON.parse(sessionStorage.getItem("vpd.auth.v1") ?? "null");
      const rsp = await fetch("/viewer-api/icd10/chapters", {
        headers: { Authorization: `Bearer ${auth?.accessToken ?? ""}` },
      });
      if (rsp.ok) setChs(await rsp.json());
    } catch { /* fallback = no chapter filter */ }
  });

  // Debounced search. 200ms so the typist feels responsive but we
  // don't hammer postgres per keystroke.
  let timer: number | undefined;
  createEffect(() => {
    const query = q();
    const chap  = chapter();
    if (timer) window.clearTimeout(timer);
    if (!query && !chap) { setHits([]); return; }
    timer = window.setTimeout(async () => {
      setLoad(true);
      try {
        const auth = JSON.parse(sessionStorage.getItem("vpd.auth.v1") ?? "null");
        const params = new URLSearchParams();
        if (query) params.set("q", query);
        if (chap)  params.set("chapter", chap);
        params.set("limit", "50");
        const rsp = await fetch(`/viewer-api/icd10/search?${params}`, {
          headers: { Authorization: `Bearer ${auth?.accessToken ?? ""}` },
        });
        setHits(rsp.ok ? await rsp.json() : []);
      } catch { setHits([]); }
      finally { setLoad(false); }
    }, 200);
  });
  onCleanup(() => { if (timer) window.clearTimeout(timer); });

  function pick(h: Hit) {
    // De-duplicate: if already assigned, no-op.
    if (p.value.some((v) => v.code === h.code)) return;
    const next: IcdValue[] = [
      ...p.value,
      {
        code:        h.code,
        code_dotted: h.code_dotted,
        long_desc:   h.long_desc,
        principal:   p.value.length === 0,   // first added is principal by default
        added_at:    new Date().toISOString(),
      },
    ];
    p.onChange(next, { action: "add", code: h.code });
    setQ("");
    setOpen(false);
  }
  function remove(code: string) {
    const before = p.value.find((v) => v.code === code);
    if (!before) return;
    let next = p.value.filter((v) => v.code !== code);
    // If we dropped the principal, promote the first remaining.
    if (before.principal && next.length > 0) {
      next = next.map((v, i) => i === 0 ? { ...v, principal: true } : { ...v, principal: false });
    }
    p.onChange(next, { action: "remove", code });
  }
  function setPrincipal(code: string) {
    const next = p.value.map((v) => ({ ...v, principal: v.code === code }));
    p.onChange(next, { action: "principal", code });
  }

  return (
    <div class="relative">
      {/* Chip row — already-picked codes */}
      <Show when={p.value.length > 0}>
        <div class="mb-1 flex flex-wrap gap-1">
          <For each={p.value}>{(v) => (
            <span class="inline-flex items-center gap-1 rounded border border-brand bg-brand/15 px-1.5 py-0.5 text-[11px]">
              <button type="button"
                      class="text-[9px] uppercase tracking-wider"
                      classList={{
                        "text-brand": !!v.principal,
                        "text-ink-700 hover:text-brand": !v.principal,
                      }}
                      title={v.principal ? "Principal diagnosis" : "Click to mark as principal"}
                      onClick={() => setPrincipal(v.code)}>
                {v.principal ? "★" : "☆"}
              </button>
              <span class="font-mono text-white">{v.code_dotted}</span>
              <span class="max-w-[260px] truncate text-ink-900" title={v.long_desc}>
                {v.long_desc}
              </span>
              <button type="button"
                      class="text-ink-700 hover:text-danger"
                      title="Remove"
                      onClick={() => remove(v.code)}>✕</button>
            </span>
          )}</For>
        </div>
      </Show>

      {/* Search input + chapter chip row */}
      <div class="flex gap-1">
        <input type="search"
               class="flex-1 rounded border border-ink-300 bg-ink-200 px-2 py-1 text-[11px] text-ink-900 focus:border-brand focus:outline-none"
               placeholder="search codes · e.g. breast ca, melanoma, C50.911"
               value={q()}
               onFocus={() => setOpen(true)}
               onInput={(e) => { setQ(e.currentTarget.value); setOpen(true); }}
               onBlur={() => { window.setTimeout(() => setOpen(false), 180); }} />
        <Show when={chapters().length > 0}>
          <select class="rounded border border-ink-300 bg-ink-200 px-2 py-1 text-[10px] text-ink-900 focus:border-brand focus:outline-none"
                  value={chapter()}
                  onChange={(e) => setChap(e.currentTarget.value)}>
            <option value="">every chapter</option>
            <For each={chapters()}>{(c) => (
              <option value={c.chapter}>{c.chapter} ({c.billable})</option>
            )}</For>
          </select>
        </Show>
      </div>

      {/* Result dropdown */}
      <Show when={open() && (q() || chapter())}>
        <div class="absolute z-40 mt-1 max-h-[320px] w-full overflow-auto rounded border border-ink-300 bg-ink-100 shadow-xl">
          <Show when={loading()}>
            <div class="px-3 py-2 text-[11px] text-ink-700">searching…</div>
          </Show>
          <Show when={!loading() && hits().length === 0}>
            <div class="px-3 py-2 text-[11px] text-ink-700">
              No codes match. Try a different term or narrow by chapter.
            </div>
          </Show>
          <For each={hits()}>{(h) => {
            const already = p.value.some((v) => v.code === h.code);
            return (
              <button type="button"
                      class="flex w-full items-start gap-2 border-b border-ink-300 px-2 py-1.5 text-left hover:bg-ink-200"
                      classList={{ "opacity-40": already }}
                      disabled={already}
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => pick(h)}>
                <span class="shrink-0 rounded bg-ink-200 px-1 py-0.5 font-mono text-[10px] text-brand">
                  {h.code_dotted}
                </span>
                <span class="min-w-0 flex-1 text-[11px] text-ink-900">
                  <span class="block truncate">{h.long_desc}</span>
                  <span class="block truncate text-[9px] text-ink-700">{h.chapter ?? ""}</span>
                </span>
                <Show when={!h.is_billable}>
                  <span class="shrink-0 rounded bg-warn/20 px-1 py-0.5 text-[9px] text-warn">header</span>
                </Show>
              </button>
            );
          }}</For>
        </div>
      </Show>
    </div>
  );
}
